import React, {useRef, useState} from "react";
import BootstrapTable from "react-bootstrap-table-next";
import ToolkitProvider from "react-bootstrap-table2-toolkit/dist/react-bootstrap-table2-toolkit";
import Pagination from "../../components/Pagination";
import { Spinner, UncontrolledTooltip, Badge } from "reactstrap";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "context/TranslationProvider";
import helpers from "utils/helpers";

function OrdersModalTable({ items = [], isLoading }) {
  const navigate = useNavigate();
  const componentRef = useRef();
  const { t, language } = useTranslation();
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [searchText, setSearchText] = useState("");

  const searchableItems = items.map((item) => {
    const senderName = item.sender_listing?.user
      ? `${item.sender_listing.user.firstName || ""} ${item.sender_listing.user.lastName || ""}`
      : "";

    const travelerName = item.traveler_listing?.user
      ? `${item.traveler_listing.user.firstName || ""} ${item.traveler_listing.user.lastName || ""}`
      : "";

    const fromPlace = item.sender_listing?.fromPlace?.name || item.traveler_listing?.fromPlace?.name || "";
    const toPlace = item.sender_listing?.toPlace?.name || item.traveler_listing?.toPlace?.name || "";

    return {
      ...item,
      _searchText: `${item.orderid || ""} ${senderName} ${travelerName} ${fromPlace} ${toPlace} ${item.status || ""}`.toLowerCase(),
    };
  });

  const finalFilteredItems = searchableItems.filter((item) =>
    item._searchText.includes(searchText.toLowerCase())
  );

  const paginatedItems = finalFilteredItems.slice(
    currentPage * rowsPerPage,
    (currentPage + 1) * rowsPerPage
  );

  const getRowStatus = (row) => {
    const fieldsToCheck = [
      row.sender_listing?.user?.firstName,
      row.traveler_listing?.user?.firstName,
      row.sender_listing?.fromPlace?.name || row.traveler_listing?.fromPlace?.name,
      row.sender_listing?.toPlace?.name || row.traveler_listing?.toPlace?.name,
      row.sender_listing?.date || row.traveler_listing?.date,
    ];

    const hasEmptyField = fieldsToCheck.some((field) => !field || field === "");

    if (hasEmptyField) return "cancelled";
    return helpers.normalizeOrderStatus(row.status) || "confirmed";
  };

  if (isLoading) {
    return (
      <div className="center-loader">
        <div className="center-loader-content">
          <Spinner/>
          <p className="mt-3 text-muted">
            {t("common.loading", "Loading....")}
          </p>
        </div>
      </div>
    );
  }

  const columns = [
    {
      dataField: "_searchText",
      text: t("table.search", "Search"),
      hidden: true,
      searchable: true,
    },
    {
      dataField: "orderid",
      text: t("table.headers.orderId", "Order ID"),
      sort: true,
      headerAlign: "left",
      align: "left",
      formatter: (cell, row) => (
        <>
          <span
            id={`orderid-${row._id}`}
            className="text-primary font-weight-bold sidenav-toggler"
            onClick={() => navigate(`/admin/all-orders/orderDetails/${row._id}`)}
          >
            {cell}
          </span>
          <UncontrolledTooltip ey={`order-${row._id}-${language}`} target={`orderid-${row._id}`}>
            {t("tooltip.viewOrder", "View order details")}
          </UncontrolledTooltip>
        </>
      ),
    },
    {
      dataField: "sender",
      text: t("page.orders.sender", "Sender"),
      formatter: (_, row) =>
        row.sender_listing?.user
          ? `${row.sender_listing.user.firstName || "-"} ${row.sender_listing.user.lastName || ""}`
          : "-",
    },
    {
      dataField: "traveler",
      text: t("page.orders.traveler", "Traveler"),
      formatter: (_, row) =>
        row.traveler_listing?.user
          ? `${row.traveler_listing.user.firstName || "-"} ${row.traveler_listing.user.lastName || ""}`
          : "-",
    },
    {
      dataField: "fromTo",
      text: t("table.headers.location", "Location"),
      formatter: (_, row) => {
        const from = row.sender_listing?.fromPlace?.name || row.traveler_listing?.fromPlace?.name || "-";
        const to = row.sender_listing?.toPlace?.name || row.traveler_listing?.toPlace?.name || "-";
        return `${from} → ${to}`;
      },
    },
    {
      dataField: "journeyDate",
      text: t("page.orders.journeyDate", "Journey Date"),
      formatter: (_, row) => {
        const date = row.sender_listing?.date || row.traveler_listing?.date;
        return date ? helpers.getFormattedDate(date, t) : "-";
      },
    },
    {
      dataField: "status",
      text: t("table.headers.status", "Status"),
      formatter: (_, row) => {
        const statusKey = getRowStatus(row);

        const color =
          helpers
            .getStatusClass(statusKey, "tableOrder")
            .replace("status-", "") || "secondary";

        return (
          <Badge className="badge-lg" color={color}>
            {t(`page.orders.status.${statusKey}`, helpers.ORDER_STATUS_API_MAP[statusKey] || statusKey)}
          </Badge>
        );
      },
    }
  ];

  return (
    <ToolkitProvider key={language} keyField="_id" data={searchableItems} columns={columns} search>
      {(props) => (
        <div className="py-4 w-full flex flex-col" ref={componentRef}>
          <div
            id="datatable-basic_filter"
            className="dataTables_filter px-4 pb-3 flex items-center justify-between"
          >
            <label className="flex items-center gap-2 mb-0">
              <span className="text-sm font-medium text-slate-600">{t("table.search", "Search")}:</span>
              <input
                className="form-control form-control-sm"
                placeholder={t("table.search", "Search")}
                value={searchText}
                onChange={(e) => {
                  setSearchText(e.target.value);
                  setCurrentPage(0);
                }}
              />
            </label>
          </div>
                <div className="table-responsive table-responsive-scroll">
                  <BootstrapTable
                    {...props.baseProps}
                    data={paginatedItems}
                    bootstrap4
                    bordered={false}
                    noDataIndication={() => (
                      <div className="text-center text-slate-500 py-4 w-full">
                        {t("page.orders.noOrders", "No Orders Found")}
                      </div>
                    )}
                  />
                </div>
                <Pagination
                  totalRows={finalFilteredItems.length}
                  rowsPerPage={rowsPerPage}
                  currentPage={currentPage}
                  onPageChange={(page) => setCurrentPage(page)}
                  onRowsPerPageChange={(size) => {
                    setRowsPerPage(size);
                    setCurrentPage(0);
                  }}
                  rowsPerPageOptions={[5, 10, 20]}
                  t={t}
                />
        </div>
      )}
    </ToolkitProvider>
  );
}

export default OrdersModalTable;
